import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Pages } from '../Pages'
import { MenuItems } from '../enums/MenuItems'
import roleApi from '../api/role/RoleApi'
import { RoleLiteDto } from '../api/role/models/RoleDto'
import LoadingSpinner from '../uiV2/common/loading/LoadingSpinner'
import Note from '../components/Note'
import { NoteType } from '../enums/NoteType'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faPencil,
    faMagnifyingGlass,
    faList,
    faMagnifyingGlassArrowRight,
    faDownload,
} from '@fortawesome/free-solid-svg-icons'
import Section from '../components/Section'
import { Toaster, toast } from 'sonner'
import { AuthHelper } from '../auth/AuthHelper'
import { UserRoles } from '../api/tenant/models/UserDto'
import { useSafeUserData } from '../stateStores/User'
import LayoutSplitView from '../components/layout/LayoutSplitView'
import NetworkSeedRightPane from '../components/manageRole/NetworkSeedRightPane'
import CompanyRightPane from '../components/manageRole/CompanyRightPane'
import RoleRightPane from '../components/manageRole/RoleRightPane'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import { ResolvedPersonLiteDto } from '../api/network/models/getNetworkScopedDtos'
import { MapExtra } from '../utils/MapExtra'
import { PersonDto, PersonLiteDto } from '../api/person/models/PersonDto'
import { CompanyDto } from '../api/company/models/CompanyDto'
import networkApi from '../api/network/NetworkApi'
import HelixSearchNetworkView from '../components/helixSearch/HelixSearchNetworkView'
import personApi from '../api/person/PersonApi'
import CandidateRightPane from '../components/CandidateRightPane'
import {
    CandidateLiteDto,
    PresentCandidateValues,
} from '../api/role/models/CandidateDto'
import companyApi from '../api/company/CompanyApi'
import CandidateResultList from '../components/CandidateResultList'
import CandidateResultItem from '../components/CandidateResultItem'
import { useManageCandidateStore } from '../components/manageRole/stateStore/manageCandidateStore'
import CandidateReview from '../uiV2/role/candidateReview/CandidateReview'
import { useNavigate } from 'react-router-dom'
import RoleCard from '../uiV2/role/RoleCard'
import CtxtLink from '../uiV2/common/links/CtxtLink'
import Button from '../uiV2/common/form/inputs/Button'
import { buildCandidatePresentMap } from '../utils/candidateUtils'

type FilterData = {
    currentPageNumber: number
    perPage: number
    shortlistedSelected: string
}

function ManageRole(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const [roleLite, setRoleLite] = useState<RoleLiteDto>()
    const [hasCandidates, setHasCandidates] = useState<boolean>(false)
    // const [isModalOpen, setIsModalOpen] = useState(false)
    const [exportCsvIsLoading, setExportCsvIsLoading] = useState(false)
    const roleId = searchParams.get('id')
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)
    const [isLoadingNetwork, setIsLoadingNetwork] = useState(false)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const { user, tenant } = useSafeUserData()
    const navigate = useNavigate()

    // Candidate review status for HM-only users
    // const [candidateReviewStatus, setCandidateReviewStatus] =
    //     useState<CandidateReviewStatus | null>(null)
    // const [isCheckingReviewStatus, setIsCheckingReviewStatus] = useState(false)
    // const [selectedNetworkLinksIds, setSelectedNetworkLinksIds] = useState<
    //     string[]
    // >([])
    // Removed unused sharedWithUsers state
    const [rightPaneMode, setRightPaneMode] = useState<
        'role' | 'candidate' | 'network_seed' | 'company'
    >('role')
    const [selectedCandidateId, setSelectedCandidateId] = useState<
        string | null
    >(null)

    const [resolvedPersons, setResolvedPersons] = useState<
        ResolvedPersonLiteDto[]
    >([])
    // Contains network links
    const personLiteCacheRef = useRef(new MapExtra<string, PersonLiteDto>())
    // Contains candidates
    const personCacheRef = useRef(new MapExtra<string, PersonDto>())
    const companyCacheRef = useRef(new MapExtra<string, CompanyDto>())
    // const [pendingHiringManagerIds, setPendingHiringManagerIds] = useState<
    //     string[]
    // >([])
    const [candidateLiteDtos, setCandidateLiteDtos] = useState<
        CandidateLiteDto[]
    >([])
    const setCandidates = useManageCandidateStore((s) => s.setCandidates)
    const setRoleLiteStore = useManageCandidateStore((s) => s.setRoleLite)
    const storeCandidates = useManageCandidateStore((s) => s.candidates)
    const storeEphemeralCandidates = useManageCandidateStore(
        (s) => s.ephemeralCandidates,
    )
    // const [sharedWith, setSharedWith] = useState<
    //     {
    //         user: SharedWithUserLite
    //         candidateReviewStatus: CandidateReviewStatus
    //     }[]
    // >([])
    // const [personById, setPersonById] = useState<
    //     MapExtra<string, PersonLiteDto>
    // >(new MapExtra())
    // ##### filter state - start #####
    // const [shortlistedSelected, setShortlistedSelected] =
    //     useState<string>('yes')
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(50)
    const [totalResultCount, setTotalResultCount] = useState<number>(0)
    // ##### filter state - end #####

    const filterRef = useRef<FilterData>({
        currentPageNumber,
        perPage,
        shortlistedSelected: 'yes',
    })
    const hasCompletedInitialLoadRef = useRef(false)
    const networkRequestIdRef = useRef(0)
    const candidatePresentById = useMemo(
        () =>
            buildCandidatePresentMap(storeCandidates, storeEphemeralCandidates),
        [storeCandidates, storeEphemeralCandidates],
    )

    const refreshRole = useCallback(
        async (isInitial = false) => {
            if (!roleId) return
            if (isInitial) setIsLoading(true)
            setIsLoadingCandidates(true)

            try {
                const role = await fetchRoleAndSetState(roleId)
                const filter = filterRef.current

                const candidates = await fetchCandidatesForRole(roleId, filter)
                const candidatePersonIds = candidates.map(
                    (c) => c.candidatePersonId,
                )

                await fetchAndCacheFullPersonData(candidatePersonIds)
                await fetchAndCacheCompaniesFromPersons(candidatePersonIds)

                setCandidateLiteDtos(candidates)
                setCandidates(candidates)

                if (candidatePersonIds.length === 0) {
                    setResolvedPersons([])
                    setIsLoadingNetwork(false)
                } else {
                    const requestId = networkRequestIdRef.current + 1
                    networkRequestIdRef.current = requestId
                    setIsLoadingNetwork(true)
                    void (async () => {
                        try {
                            const resolvedNetwork =
                                await fetchAndCacheResolvedNetwork(
                                    role,
                                    candidatePersonIds,
                                )
                            if (networkRequestIdRef.current === requestId) {
                                setResolvedPersons(resolvedNetwork)
                            }
                        } catch (networkError) {
                            console.error(
                                'Error loading network data:',
                                networkError,
                            )
                        } finally {
                            if (networkRequestIdRef.current === requestId) {
                                setIsLoadingNetwork(false)
                            }
                        }
                    })()
                }
            } catch (error) {
                console.error('Error loading role data:', error)
                setIsLoadingNetwork(false)

                if (
                    error instanceof Error &&
                    error.message.includes('Role not found')
                ) {
                    navigate('/roles', { replace: true })
                    return
                }

                // Check for permission-related error messages first
                if (
                    error instanceof Error &&
                    error.message.includes('permission')
                ) {
                    navigate('/roles')
                    return
                }

                // Check if it's a 401 Unauthorized error
                if (error && typeof error === 'object' && 'response' in error) {
                    const axiosError = error as any
                    if (axiosError.response?.status === 401) {
                        navigate('/roles')
                        return
                    }
                }

                setErrMsg(
                    `Failed to load role data: ${
                        error instanceof Error ? error.message : 'Unknown error'
                    }`,
                )
            } finally {
                if (isInitial) setIsLoading(false)
                setIsLoadingCandidates(false)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate, roleId, setCandidates, setRoleLiteStore],
    )

    async function fetchRoleAndSetState(roleId: string): Promise<RoleLiteDto> {
        const { role } = await roleApi.getLite({
            roleId,
            includeHiringTeamIds: true,
            includeNetworkLinkIds: true,
            includeSharedWithUserIds: true,
        })
        setRoleLite(role)
        setRoleLiteStore(role)

        // const selectedIds = [
        //     ...(role.networkLinkIds ?? []),
        //     ...(role.sharedWithUserIds ?? []),
        // ]
        // setSelectedNetworkLinksIds(selectedIds)

        const hasCandidates = await roleApi.getCandidateCountForRole({
            roleId,
            shortlisted: PresentCandidateValues.YES,
        })
        setHasCandidates(hasCandidates > 0)

        // Removed fetching and setting sharedWithUsers as it is unused
        await roleApi.getSharedWithUsersForRole({
            roleId,
        })

        // const [sharedWithRes, personsLite] = await Promise.all([
        //     roleApi.getSharedWithUsersWithStatus({ roleId }),
        //     personApi.getPersonsLite({
        //         personIds: role.hiringTeamIds ?? [],
        //     }),
        // ])
        // setSharedWith(sharedWithRes.sharedWith)
        // setPersonById(new MapExtra(personsLite.persons.map((p) => [p.id, p])))

        return role
    }

    async function fetchCandidatesForRole(roleId: string, filter: FilterData) {
        const { candidates } = await roleApi.getCandidates({
            roleId,
            pageSize: filter.perPage,
            pageNumber: filter.currentPageNumber,
            orderBy: 'combined',
            presentFilter: filter.shortlistedSelected as
                | PresentCandidateValues
                | 'all',
            interestedFilter: 'all',
        })

        const candidateCount = await roleApi.getCandidateCountForRole({
            roleId,
            shortlisted: filter.shortlistedSelected,
        })
        setTotalResultCount(candidateCount)

        return candidates
    }

    async function fetchAndCacheFullPersonData(personIds: string[]) {
        const missingIds = personIds.filter(
            (id) => !personCacheRef.current.has(id),
        )
        if (!missingIds.length) return

        const results = await Promise.allSettled(
            missingIds.map((id) => personApi.getPersonById(id)),
        )
        for (const result of results) {
            if (result.status === 'fulfilled') {
                personCacheRef.current.set(
                    result.value.person.id,
                    result.value.person,
                )
            } else {
                console.warn('Failed to load person', result.reason)
            }
        }
    }

    async function fetchAndCacheCompaniesFromPersons(personIds: string[]) {
        const companyIdsToFetch = new Set<string>()

        for (const id of personIds) {
            const person = personCacheRef.current.get(id)
            person?.workedAts?.forEach((w) => {
                if (w.companyId && !companyCacheRef.current.has(w.companyId)) {
                    companyIdsToFetch.add(w.companyId)
                }
            })
        }

        if (companyIdsToFetch.size === 0) return

        const { companies } = await companyApi.getCompanies({
            companyIds: Array.from(companyIdsToFetch),
        })
        for (const company of companies) {
            companyCacheRef.current.set(company.id, company)
        }
    }

    async function fetchAndCacheResolvedNetwork(
        role: RoleLiteDto,
        candidatePersonIds: string[],
    ) {
        const seedPersonIds = [
            ...(role.hiringTeamIds ?? []),
            ...(role.networkLinkIds ?? []),
        ]

        if (seedPersonIds.length === 0 || candidatePersonIds.length === 0)
            return []

        const { resolvedPersonLites } = await networkApi.getNetworkScoped({
            seedPersonIds,
            resolvedPersonIds: candidatePersonIds,
        })

        const missingSeedIds = new Set<string>()

        for (const resolved of resolvedPersonLites) {
            for (const detail of resolved.connectionDetails) {
                const id = detail.seedPersonId
                if (!personLiteCacheRef.current.has(id)) {
                    missingSeedIds.add(id)
                }
            }
        }

        if (missingSeedIds.size > 0) {
            try {
                const { persons } = await personApi.getPersonsById(
                    Array.from(missingSeedIds),
                )

                for (const person of persons) {
                    personLiteCacheRef.current.set(person.id, person)
                }
            } catch (err) {
                console.warn(
                    `Failed to batch load seed persons ${Array.from(missingSeedIds).join(',')}`,
                    err,
                )
            }
        }

        return resolvedPersonLites
    }

    // Check candidate review status for HM-only users
    // useEffect(() => {
    //     const checkCandidateReviewStatus = async () => {
    //         if (!roleId || !user) return

    //         // Only check for HM-only users (not TA)
    //         if (
    //             !AuthHelper.userHasRoleForTenant(
    //                 user,
    //                 UserRoles.HM,
    //                 tenant.id,
    //             ) ||
    //             AuthHelper.userHasRoleForTenant(user, UserRoles.TA, tenant.id)
    //         ) {
    //             return
    //         }

    //         setIsCheckingReviewStatus(true)
    //         try {
    //             const response = await roleApi.getCandidateReviewStatus({
    //                 roleId,
    //             })
    //             setCandidateReviewStatus(response.candidateReviewStatus)
    //         } catch (error) {
    //             console.error('Error checking candidate review status:', error)

    //             // Check if it's a 401 Unauthorized error
    //             if (error && typeof error === 'object' && 'response' in error) {
    //                 const axiosError = error as any
    //                 if (axiosError.response?.status === 401) {
    //                     // User doesn't have permission to view this role, redirect to roles page
    //                     navigate('/roles')
    //                     return
    //                 }
    //             }

    //             // Check for permission-related error messages as fallback
    //             if (
    //                 error instanceof Error &&
    //                 error.message.includes('permission')
    //             ) {
    //                 navigate('/roles')
    //                 return
    //             }

    //             setErrMsg('You do not have permission to view this role.')
    //         } finally {
    //             setIsCheckingReviewStatus(false)
    //         }
    //     }

    //     checkCandidateReviewStatus()
    // }, [roleId, user, navigate, tenant.id])

    useEffect(() => {
        if (!roleId) return

        const initialPageNumber = 1
        const initialPerPage = 50

        setCurrentPageNumber(initialPageNumber)
        setPerPage(initialPerPage)
        filterRef.current = {
            currentPageNumber: initialPageNumber,
            perPage: initialPerPage,
            shortlistedSelected: 'yes',
        }
        hasCompletedInitialLoadRef.current = false

        const loadInitial = async () => {
            await refreshRole(true)
            hasCompletedInitialLoadRef.current = true
        }

        void loadInitial()
    }, [refreshRole, roleId])

    useEffect(() => {
        if (
            !roleId ||
            isLoading ||
            isLoadingCandidates ||
            hasCandidates ||
            !hasCompletedInitialLoadRef.current
        ) {
            return
        }

        // No candidates saved yet; send user to search to add some
        navigate(
            `${Pages.HelixSearchV2}?roleId=${roleId}&autoOpenFilters=true`,
            {
                replace: true,
            },
        )
    }, [hasCandidates, isLoading, isLoadingCandidates, navigate, roleId])

    useEffect(() => {
        if (!hasCompletedInitialLoadRef.current) return

        filterRef.current = {
            currentPageNumber,
            perPage,
            shortlistedSelected: 'yes',
        }
        refreshRole(false) // soft load for candidates
    }, [currentPageNumber, perPage, refreshRole])

    // async function handleHmRemoved(candidateId: string) {
    //     try {
    //         await roleApi.removeCandidateFromRole({
    //             roleId: roleId!,
    //             personId: candidateId,
    //         })
    //         refreshRole()
    //     } catch (error) {
    //         console.error(error)
    //         toast.error(`Failed to remove candidate from role.`)
    //     }
    // }

    // async function shortlistedSelectedChanged(shortlistedSelected: string) {
    //     setShortlistedSelected(shortlistedSelected)
    //     setCurrentPageNumber(1)
    //     await refreshRole()
    // }

    const pageChanged = useCallback((pageNumber: number) => {
        setCurrentPageNumber(pageNumber)
    }, [])

    const perPageChanged = useCallback((newPerPage: number) => {
        setPerPage(newPerPage)
        setCurrentPageNumber(1)
    }, [])

    // async function addHiringManagerModalClosed() {
    //     setPendingHiringManagerIds([])
    //     setIsModalOpen(false)
    //     await refreshRole()
    // }

    async function exportCsv() {
        if (!roleId) {
            return
        }

        try {
            setExportCsvIsLoading(true)
            const blob = await roleApi.exportCandidatesCsv(roleId)
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = `contxt_data_role_${roleId}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(downloadUrl)
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem exporting the CSV, please try again.`,
                { duration: 5000 },
            )
        } finally {
            setExportCsvIsLoading(false)
        }
    }

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <LoadingSpinner />
            </LayoutSingleView>
        )
    }

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Note type={NoteType.ERROR}>{errMsg}</Note>
            </LayoutSingleView>
        )
    }

    // For HM-only users, check candidate review status and show CandidateReview if not complete
    if (
        user &&
        AuthHelper.userHasRoleForTenant(user, UserRoles.HM, tenant.id) &&
        !AuthHelper.userHasRoleForTenant(user, UserRoles.TA, tenant.id)
        // candidateReviewStatus !== null &&
        // candidateReviewStatus !== CandidateReviewStatus.COMPLETE
    ) {
        return <CandidateReview />
    }

    return (
        <LayoutSplitView
            activeMenuItem={MenuItems.Roles}
            rightPane={
                roleId && roleLite
                    ? (() => {
                          switch (rightPaneMode) {
                              case 'role':
                                  return <RoleRightPane roleLite={roleLite} />
                              case 'candidate':
                                  return selectedCandidateId ? (
                                      <CandidateRightPane
                                          candidateId={selectedCandidateId}
                                          networkLinksPersonLiteCache={
                                              personLiteCacheRef.current
                                          }
                                          candidatesPersonCache={
                                              personCacheRef.current
                                          }
                                          companyCache={companyCacheRef.current}
                                          candidateConnections={
                                              resolvedPersons.find(
                                                  (p) =>
                                                      p.personId ===
                                                      selectedCandidateId,
                                              )?.connectionDetails ?? []
                                          }
                                          roleLite={roleLite}
                                      />
                                  ) : null
                              case 'network_seed':
                                  return <NetworkSeedRightPane />
                              case 'company':
                                  return <CompanyRightPane />
                              default:
                                  throw new Error(
                                      `Invalid mode: ${rightPaneMode}`,
                                  )
                          }
                      })()
                    : undefined
            }
        >
            {roleLite && (
                <>
                    <div className="flex gap-2 mb-4 justify-between flex-wrap items-center">
                        <CtxtLink to={`${Pages.Roles}`} className="">
                            View All Roles
                        </CtxtLink>
                        {/* <Button
                            style="secondary"
                            type="link"
                            to={`${Pages.HelixSearch}?roleId=${roleId}`}
                        >
                            <FontAwesomeIcon
                                icon={faMagnifyingGlass}
                                className={`mr-2`}
                            />
                            Find Candidates
                        </Button> */}
                        <div className="flex gap-2 flex-wrap">
                            <Button
                                style="secondary"
                                type="button"
                                onClick={() => setRightPaneMode('role')}
                            >
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlassArrowRight}
                                    className={`mr-2`}
                                />
                                Recent Searches
                            </Button>
                            <Button
                                style="secondary"
                                type="link"
                                to={`${Pages.UpdateRole}?id=${roleId}`}
                            >
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    className={`mr-2`}
                                />
                                Edit Role
                            </Button>
                            {resolvedPersons.length > 0 && (
                                <Button
                                    style="secondary"
                                    type="button"
                                    onClick={exportCsv}
                                    enabled={!exportCsvIsLoading}
                                >
                                    {exportCsvIsLoading ? (
                                        <LoadingSpinner spinnerSize={24} />
                                    ) : (
                                        <FontAwesomeIcon icon={faDownload} />
                                    )}
                                    <div className="ml-2">
                                        Export Candidates
                                    </div>
                                </Button>
                            )}
                            <Button
                                style="primary"
                                type="link"
                                to={`${Pages.HelixSearchV2}?roleId=${roleId}`}
                            >
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className={`mr-2`}
                                />
                                Find Candidates
                            </Button>
                        </div>
                    </div>
                    <RoleCard
                        role={roleLite}
                        clickable={false}
                        showJobDescription={true}
                    />
                </>
            )}
            {/* <Section
                subtitle="Role"
                title={roleLite?.jobTitle}
                buttons={
                    <>
                        {roleLite &&
                            AuthHelper.userHasSomeRolesForTenant(
                                user,
                                [UserRoles.TA, UserRoles.HM],
                                tenant.id,
                            ) && (
                                <>
                                    <Button
                                        style="secondary"
                                        type="link"
                                        to={`${Pages.UpdateRole}?id=${roleId}`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPencil}
                                            className={`mr-2`}
                                        />
                                        Edit
                                    </Button>
                                    <Button
                                        style="secondary"
                                        type="link"
                                        to={`${Pages.HelixSearchV2}?roleId=${roleId}`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlass}
                                            className={`mr-2`}
                                        />
                                        Search Candidates
                                    </Button>
                                    <Button
                                        style="secondary"
                                        type="button"
                                        onClick={() => setRightPaneMode('role')}
                                    >
                                        <FontAwesomeIcon
                                            icon={faMagnifyingGlassArrowRight}
                                            className={`mr-2`}
                                        />
                                        Recent Searches
                                    </Button>
                                    {user.isProductAdmin &&
                                        resolvedPersons.length > 0 && (
                                            <Button
                                                style="secondary"
                                                type="button"
                                                onClick={exportCsv}
                                                enabled={!exportCsvIsLoading}
                                            >
                                                {exportCsvIsLoading ? (
                                                    <LoadingSpinner
                                                        spinnerSize={24}
                                                    />
                                                ) : (
                                                    <FontAwesomeIcon
                                                        icon={faFileExport}
                                                    />
                                                )}
                                                <div className="ml-2">
                                                    Export CSV
                                                </div>
                                            </Button>
                                        )}
                                </>
                            )}
                    </>
                }
            > */}
            {/* {roleLite && selectedNetworkLinksIds.length === 0 && (
                    <NoRecords
                        imgSrc="connecting_teams.svg"
                        caption="Add a hiring manager to start finding quality candidates"
                    >
                        <Button
                            style="primary"
                            type="button"
                            classes="mt-4"
                            onClick={() => setIsModalOpen(true)}
                        >
                            <FontAwesomeIcon
                                icon={faPlusCircle}
                                className={`mr-2`}
                            />
                            Add Hiring Manager
                        </Button>
                    </NoRecords>
                )} */}

            {/* {roleLite &&
                    selectedNetworkLinksIds.length > 0 &&
                    AuthHelper.userHasSomeRolesForTenant(
                        user,
                        [UserRoles.HM, UserRoles.TA],
                        tenant.id,
                    ) && (
                        <Section
                            title="Shared With"
                            className="mt-6"
                            titleType="h2"
                            collapsible={true}
                            defaultCollapsed={false}
                            buttons={
                                <Button
                                    style="secondary"
                                    type="button"
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    <FontAwesomeIcon
                                        icon={faPeopleGroup}
                                        className={`mr-2`}
                                    />
                                    {`Share`}
                                </Button>
                            }
                        >
                            <NetworkSeeds
                                role={roleLite}
                                ephemeralSeedIds={pendingHiringManagerIds}
                            />
                        </Section>
                    )} */}

            {roleLite && hasCandidates && (
                <div>
                    {/* <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                            <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                <FontAwesomeIcon
                                    icon={faFilter}
                                    className="text-ctxt-blue"
                                />
                                Filters
                            </div>
                            <CandidateSearchFilters
                                roleId={roleId}
                                shortlistedSelected={shortlistedSelected}
                                shortlistedSelectedChanged={
                                    shortlistedSelectedChanged
                                }
                            />
                        </div> */}

                    {isLoadingCandidates ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <>
                            <Section
                                title="Saved Candidates"
                                className="mt-6"
                                titleType="h2"
                            >
                                <div className="mt-4">
                                    {isLoadingNetwork &&
                                    resolvedPersons.length === 0 ? (
                                        <div className="flex justify-center py-12">
                                            <LoadingSpinner />
                                        </div>
                                    ) : (
                                        <HelixSearchNetworkView
                                            resolvedPersons={resolvedPersons}
                                            networkLinksPersonCache={
                                                personLiteCacheRef.current
                                            }
                                            candidatesPersonCache={
                                                personCacheRef.current
                                            }
                                            companyCache={
                                                companyCacheRef.current
                                            }
                                            candidatePresentById={
                                                candidatePresentById
                                            }
                                            onCandidateSelection={(
                                                candidateId,
                                            ) => {
                                                setSelectedCandidateId(
                                                    candidateId,
                                                )
                                                setRightPaneMode('candidate')
                                            }}
                                            hasRole={true}
                                        />
                                    )}
                                </div>
                            </Section>

                            <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
                                <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 pb-2 mb-4">
                                    <FontAwesomeIcon
                                        icon={faList}
                                        className="text-ctxt-blue"
                                    />
                                    Candidate List
                                </div>
                                <CandidateResultList
                                    candidates={candidateLiteDtos}
                                    personCache={personCacheRef.current}
                                    personLiteCache={personLiteCacheRef.current}
                                    resolvedPersonLites={resolvedPersons}
                                    companyCache={companyCacheRef.current}
                                    currentPageNumber={currentPageNumber}
                                    perPage={perPage}
                                    totalResultCount={totalResultCount}
                                    pageChanged={pageChanged}
                                    perPageChanged={perPageChanged}
                                    getCandidateRank={(c) => c.rank ?? 0}
                                    ResultItemComponent={(props) => (
                                        <CandidateResultItem
                                            {...props}
                                            roleId={roleId!}
                                        />
                                    )}
                                    onCandidateSelection={(candidateId) => {
                                        setSelectedCandidateId(candidateId)
                                        setRightPaneMode('candidate')
                                    }}
                                    roleId={roleId ?? undefined}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* {isModalOpen &&
                    AuthHelper.userHasSomeRolesForTenant(
                        user,
                        [UserRoles.HM, UserRoles.TA],
                        tenant.id,
                    ) && (
                        <InviteUsersModal
                            onClose={addHiringManagerModalClosed}
                            roleTitle={roleLite!.jobTitle}
                            roleId={roleId!}
                            tenantId={tenant.id}
                            sharedWith={sharedWith}
                            personById={personById}
                            tenantCompanyIds={tenant.companyIds}
                        />
                    )} */}
            {/* </Section> */}
            <Toaster />
        </LayoutSplitView>
    )
}

export default ManageRole
